import React, {useContext, useEffect, useReducer} from "react";
import {useMutation, useQuery} from "react-apollo";
import {OrgDataContext} from "../../../../context/orgData";
import {Mutations, MutationTypes, Queries, QueryTypes} from "./graphql";
import * as State from './state';
import {Helper, SearchEvent, SearchEventTypes} from './state';
import {ApiInterface} from "./api";
import {ScheduledPaxCgoAdderProps} from "./scheduled-paxcgo-adder";
import {ScheduledGroup, ScheduledPaxCgoGroupV2Leg} from "../../../../common/types/schedule";
import {PassengerEditor} from "../../../PassengerEditor";
import {Alert, Button, Input, message, Modal, Select} from "antd";
import {cloneDeep, intersection} from "lodash";
import {CgoNode, PaxNode} from "../../../../common/types/carriable";
import {
    buildLocNameMapFromPaxCgoLists,
    computeLegWeightsAndCounts,
    getFlightLegManagerFromFlightQuery,
    graphqlCgoToFlightManCgo,
    graphqlPaxToFlightManPax
} from "../../../../common/flight-leg/util";
import {ButtonProps} from "antd/lib/button";
import {SearchProps} from "antd/lib/input";
import {SelectProps} from "antd/lib/select";
import {AlertProps} from "antd/lib/alert";
import {cleanGraphQLErrorMsg} from "../../../../common/util";
import {FlightLeg} from "../../../../schema";
import useApolloClientHelper from "../../../../hooks/useApolloClientHelper";

type RenderFunc<Props> = (props?: Partial<Props>) => React.ReactElement<Props>

type ReturnType = {
    api: ApiInterface,

    /**
     * Enhances the ScheduledPaxCgoAdder component to consume the ApiInterface.
     * Usage: spread the componentProps object into the props of the ScheduledPaxCgoAdder component.
     * Example:
     *      <ScheduledPaxCgoAdder {...hookVariable.componentProps} />
     */
    componentProps: ScheduledPaxCgoAdderProps,

    /**
     * Renders child components for the UI.
     * These components are optional and not necessary for the hook to work.
     *
     * TODO:    This is probably an anti-pattern now that I look at it. Change each of these into components that we can
     *          spread props into similar to "componentProps"
     */
    render: {
        /**
         * Displays passenger editor popup
         */
        paxModal: () => React.ReactElement,

        /**
         * Button that allows user to add selected passengers/cargo to the flight.
         */
        addButton: RenderFunc<ButtonProps>,

        /**
         * Search query used to filter results
         */
        searchInput: RenderFunc<SearchProps>,

        /**
         * Allows user to change the fields used to compare against their search query.
         */
        searchTypeSelect: RenderFunc<SelectProps>,

        /**
         * Shows an alert saying filters are applied with the option to clear them.
         */
        filtersAppliedAlert: RenderFunc<AlertProps>,

        /**
         * Displays and error if the data fetch failed
         */
        errorAlert: RenderFunc<AlertProps>,
    }
}

export type PartialFlightLeg = Pick<FlightLeg, 'paxIDs' | 'cgoIDs' | 'departureID' | 'departure' | 'destination' | 'destinationID'>

export interface PartialFlight {
    paxIDList: string[],
    cgoIDList: string[],
    legs: PartialFlightLeg[]
}

interface Options {

    /**
     * Callback when the flight gets updated successfully
     */
    onFlightUpdated?: () => void,

    /**
     * Override specific behaviors of the scheduled-paxcgo-adder
     */
    override?: {

        /**
         * Overrides how a flight computes legs and paxIDList and cgoIDList.
         * Uses auto-assign algorithm by default to compute legs.
         * The override may save inside its review dialog with the supplied callback,
         * or return a partial flight for this hook to save. Returning void cancels
         * or indicates the dialog already saved; it must not trigger another mutation.
         *
         * @param paxnodes - Scheduled passengers being added.
         * @param cgonodes - Scheduled cargo being added.
         * @param save - Persists a reviewed proposal while allowing retry inside the dialog.
         */
        addEntitiesToFlight?: (
            paxnodes: PaxNode[],
            cgonodes: CgoNode[],
            save: (flight: PartialFlight) => Promise<void>
        ) => Promise<PartialFlight | void>;
    },

    isVisible?: boolean
}

/**
 * Build a map of an entity ID to its object for future reference.
 * @param groups
 */
function buildEntityIDMap(groups: ScheduledGroup[]){
    const map = new Map<string, PaxNode | CgoNode>();

    function add(entity: PaxNode | CgoNode){
        map.set(entity._id, entity);
    }

    for (let group of groups) {
        for (let leg of group.legs) {
            leg.paxnodeDocs.forEach(add);
            leg.cgonodeDocs.forEach(add);
        }
    }

    return map;
}

/**
 * React hook that allows child components to consume the ApiInterface as defined in api.ts.
 */
export function useScheduledPaxCgoAdderState(isVisible: boolean, flightId: string, flightDate: string, options?: Options): ReturnType{

    const [ state, dispatch ] = useReducer(State.Reducer, State.DEFAULT_STATE);
    const orgData = useContext(OrgDataContext);
    const apolloHelper = useApolloClientHelper();

    const queryResult = useQuery<
        QueryTypes['GetScheduledGroups']['Data'],
        QueryTypes['GetScheduledGroups']['Vars']
    >(Queries.GetScheduledGroups, {
        variables: {
            flightid: flightId,
            tpID: orgData.getOrgIDByType('transporter'),
            customerID: orgData.getOrgIDByType('customer'),
            scheduledFlightDate: flightDate
        },
        skip: !flightId || !flightDate || !isVisible,
        fetchPolicy: 'network-only'
    });

    useEffect(() => {
        if (isVisible){
            queryResult.refetch()
                .then(() => (
                    queryResult.startPolling(10_000) // 10 seconds
                ));
        }
        else {
            queryResult.stopPolling();
        }
    }, [isVisible]);

    const flight = queryResult.data?.flight;
    const groups = queryResult.data?.groups || [];

    const [ addPaxToFlightMutation, { loading: saving } ] = useMutation<
        MutationTypes['AddPaxToFlight']['Data'],
        MutationTypes['AddPaxToFlight']['Vars']
    >(Mutations.AddPaxToFlight);

    /**
     * Adds pax/cgo to a flight.
     * @param paxnodeIDs
     * @param cgonodeIDs
     */
    async function handleAddEntities(paxnodeIDs: string[], cgonodeIDs: string[]){

        let partialFlightData: PartialFlight = {
            paxIDList: [],
            cgoIDList: [],
            legs: []
        }

        const paxnodes = await apolloHelper.getScheduledPaxByIds(paxnodeIDs);
        const cgonodes = await apolloHelper.getScheduledCgoByIds(cgonodeIDs);

        let saved = false;
        const finish = () => {
            options?.onFlightUpdated?.();
            dispatch({category: 'SELECTION', type: 'ENTITY', action: 'CLEAR'});
            queryResult.refetch();
        };

        /** Recomputes leg loads and persists the reviewed proposal through the existing mutation. */
        const persist = async (partialFlightData: PartialFlight) => {
            const legs = await Promise.all(
                partialFlightData.legs.map(async (partialLeg) => {
                    const paxNodes = await apolloHelper.getScheduledPaxByIds(partialLeg.paxIDs);
                    const cgoNodes = await apolloHelper.getScheduledCgoByIds(partialLeg.cgoIDs);

                    return {
                        ...partialLeg,
                        ...computeLegWeightsAndCounts(paxNodes, cgoNodes),
                    };
                })
            );

            const legsStr = JSON.stringify(Object.assign({}, legs));

            await addPaxToFlightMutation({
                variables: {
                    flight: {
                        _id: flight._id,
                        customerID: flight.customerID._id,
                        departureID: departureID,
                        tpID: orgData.transporter._id,
                        ...partialFlightData,
                        legs: legsStr,
                    },
                    paxIDs: intersection(partialFlightData.paxIDList, paxnodeIDs),
                    cgoIDs: intersection(partialFlightData.cgoIDList, cgonodeIDs),
                    tpID: orgData.getOrgIDByType('transporter'),
                },
            });
            saved = true;
        };

        if (options?.override?.addEntitiesToFlight){
            const proposal = await options.override.addEntitiesToFlight(
                paxnodes,
                cgonodes,
                persist
            );
            if (proposal) {
                try {
                    await persist(proposal);
                } catch (error) {
                    message.error('Failed to add PAX/CGO to flight');
                    return;
                }
            }
            if (saved) finish();

            return;
        }
        else {

            let flManager = getFlightLegManagerFromFlightQuery(flight, buildLocNameMapFromPaxCgoLists(paxnodes, cgonodes));
            if (paxnodes?.length > 0){
                paxnodes.forEach((pax) => {
                    flManager.addPassenger(graphqlPaxToFlightManPax(pax))
                })
            }
            if (cgonodes?.length > 0){
                cgonodes.forEach((cgo) => {
                    flManager.addCgo(graphqlCgoToFlightManCgo(cgo))
                })
            }

            var departureID = flManager.getOrigin();
            if (!departureID){
                message.error("Cannot add a group with no departure on a flight.")
                return
            }
            let newLegs = flManager.buildFlightLegs();

            partialFlightData = {
                paxIDList: flManager.getPaxIDList(),
                cgoIDList: flManager.getCgoIDList(),
                legs: newLegs
            }
        }

        try {
            await persist(partialFlightData);
        } catch (error) {
            message.error('Failed to add PAX/CGO to flight');
            return;
        }

        finish();

    }

    // Consumes groups list and filters according to user filters.
    function getGroups() {
        // Apply filters if necessary
        let userFilter = state.search[state.activeSearch]?.split(/[,| ]/) ?? [];

        // Filter out empty strings
        userFilter = userFilter.filter(query => query);

        function match(str: string){
            if (!userFilter.length) return true;
            return userFilter.every(word => str?.includes(word))
        }

        const entityFilters = [
            function filterEntityByName(entity: PaxNode | CgoNode){
                if (!userFilter || state.activeSearch !== 'NAME') return true;

                let name = 'name' in entity ? entity.name : undefined;

                if (!name){
                    // Might be a PaxNode. Get the last and first name.
                    name = 'lastName' in entity || 'firstName' in entity ? `${entity.lastName} ${entity.firstName}` : '';
                }

                name = name.trim().toLowerCase();

                return match(name)
            },
            function filterEntityByEmployer(entity: PaxNode | CgoNode){
                if (!userFilter || state.activeSearch !== 'EMPLOYER') return true;

                const name = 'employerID' in entity ? String(entity.employerID?.name) : ''
                    .trim()
                    .toLowerCase();

                return match(name)
            }
        ]

        // List of filters that can be applied to legs
        const legFilters = [
            function filterLegByAnything(leg: ScheduledPaxCgoGroupV2Leg) {
                if (!userFilter|| state.activeSearch !== 'LOCATION') return true;

                return (
                    match(String(leg.departureDoc?.name).toLowerCase()) ||
                    match(String(leg.destinationDoc?.name).toLowerCase())
                )
            }
        ]

        function transformLeg(leg: ScheduledPaxCgoGroupV2Leg){
            const legCpy = cloneDeep(leg) as ScheduledPaxCgoGroupV2Leg;
            legCpy.cgonodeIDs = [];
            legCpy.cgonodeDocs = [];
            legCpy.paxnodeIDs = [];
            legCpy.paxnodeDocs = [];

            function addEntity(entity: PaxNode | CgoNode){
                if (!entity) return;

                const ctMap = {
                    'flytsuite.paxnode': 'paxnode',
                    'flytsuite.cgonode': 'cgonode'
                }

                const type = ctMap[entity.classType];
                if (!type) return;

                legCpy[type + 'Docs'].push(entity);
                legCpy[type + 'IDs'].push(entity._id);
            }

            const entityList = [...leg.paxnodeDocs, ...leg.cgonodeDocs];

            for (let entity of entityList) {
                let shouldAdd = true;
                for (let entityFilter of entityFilters) {
                    if (!entityFilter(entity)){
                        shouldAdd = false;
                        break;
                    }
                }
                if (shouldAdd){
                    addEntity(entity);
                }
            }

            return legCpy;
        }

        function transformGroup(group: ScheduledGroup){
            const groupCpy = cloneDeep(group);
            groupCpy.legs = [];

            for (let leg of group.legs) {
                for (let legFilter of legFilters) {
                    if (legFilter(leg)){
                        const legCpy = transformLeg(leg);
                        if (legCpy.paxnodeIDs.length || legCpy.cgonodeIDs.length){
                            groupCpy.legs.push(legCpy);
                        }
                        else
                        {
                            break;
                        }
                    }
                }
            }
            return groupCpy;
        }

        return groups.map(transformGroup)

            // Filter out groups with no legs
            .filter(group => group.legs.length)

    }

    // Implement ApiInterface
    const api: ApiInterface = {
        data: {
            loading: queryResult.loading,
            error: queryResult.error,
            refetch: queryResult.refetch,
            flight: {
                get: () => flight
            },
            groups: {
                get: getGroups,
            }
        },
        selection: (type) => ({
            get: () => {
                switch (type){
                    case "ENTITY":
                        return state.selectedEntities;
                }
            },
            set: (keys) => (
                dispatch({ category: 'SELECTION', type: type, action: 'SET', payload: { keys } })
            ),
            add: (keys) => (
                dispatch({ category: 'SELECTION', type: type, action: 'ADD', payload: { keys } })
            ),
            remove: (keys) => (
                dispatch({ category: 'SELECTION', type: type, action: 'REMOVE', payload: { keys } })
            ),
            clear: () => {
                dispatch({ category: 'SELECTION', type: type, action: 'CLEAR' })
            },
            toggle: (keys) => {
                dispatch({ category: 'SELECTION', type: type, action: 'TOGGLE', payload: { keys } })
            }
        }),

        /**
         * Actions that a user wants to happen such as clicking a button to add pax to a flight.
         */
        action: {
            flight: {
                addEntities: handleAddEntities,
                addSelectedEntities: async () => {
                    const paxNodes = (await apolloHelper.getScheduledPaxByIds(state.selectedEntities))
                        .map(({ _id }) => _id);
                    const cgoNodes = (await apolloHelper.getScheduledCgoByIds(state.selectedEntities))
                        .map(({ _id }) => _id);

                    await handleAddEntities(paxNodes, cgoNodes);
                },

                // True while the flight is being updated
                saving: saving
            },
            groupCollapseKeys: {
                get: () => state.openGroupPanels,
                set: (keys) => (
                    dispatch({
                        category: 'ACTION',
                        type: 'COLLAPSE.GROUPS',
                        action: 'SET',
                        payload: { panelKeys: keys }
                    })
                )
            },
            legCollapseKeys: {
                get: (group) => state.openLegPanels[Helper.panelKey.stringify(group)],
                set: (group, keys) => (
                    dispatch({
                        category: 'ACTION',
                        type: 'COLLAPSE.LEGS',
                        action: 'SET',
                        payload: {
                            group: group,
                            panelKeys: keys
                        }
                    })
                )
            },
            collapse: new class {
                expandAll = () => {
                    const groupKeys = groups.map(Helper.panelKey.stringify);
                    dispatch({
                        category: 'ACTION',
                        type: 'COLLAPSE.GROUPS',
                        action: 'SET',
                        payload: { panelKeys: groupKeys }
                    });

                    for (let group of groups) {
                        const legKeys = group.legs.map(Helper.legKey.stringify);
                        dispatch({
                            category: 'ACTION',
                            type: 'COLLAPSE.LEGS',
                            action: 'SET',
                            payload: {
                                group: group,
                                panelKeys: legKeys
                            }
                        })
                    }
                };

                collapseAll = () => {
                    dispatch({
                        category: 'ACTION',
                        type: 'COLLAPSE.GROUPS',
                        action: 'SET',
                        payload: { panelKeys: [] }
                    });
                    for (let group of groups) {
                        dispatch({
                            category: 'ACTION',
                            type: 'COLLAPSE.LEGS',
                            action: 'SET',
                            payload: {
                                group: group,
                                panelKeys: []
                            }
                        })
                    }
                };

                toggleAll = () => {
                    if (this.isAllExpanded())
                        this.collapseAll();
                    else
                        this.expandAll()
                };

                isAllExpanded = () => {
                    return state.openGroupPanels.length >= groups.length
                }
            },
            paxModal: {
                open: (paxData) => dispatch({
                    category: 'ACTION',
                    type: 'MODAL.PAX',
                    action: 'OPEN',
                    payload: {
                        paxData: paxData
                    }
                }),
                close: () => dispatch({
                    category: 'ACTION',
                    type: 'MODAL.PAX',
                    action: 'CLOSE'
                }),
                isOpen: state.paxModal.open,
                paxData: state.paxModal.paxData
            },
            isnDrawer: {
                open: (paxData) => dispatch({
                    category: 'ACTION',
                    type: 'DRAWER.ISN',
                    action: 'OPEN',
                    payload: {
                        paxData: paxData
                    }
                }),
                close: () => dispatch({
                    category: 'ACTION',
                    type: 'DRAWER.ISN',
                    action: 'CLOSE'
                }),
                isOpen: state.isnDrawer.open,
                paxData: state.isnDrawer.paxData
            }
        },
        search: {
            get: (type) => ({
                get: () => {
                    return state.search[type] || ''
                },
                set: (text) => (
                    dispatch({ category: 'SEARCH', type: type, action: 'SET', payload: { text } })
                )
            }),
            clearAll: () => (
                dispatch({ category: 'ACTION', type: 'SEARCH', action: 'CLEAR' })
            ),
            filterApplied: () => (
                !!Object.values(state.search)
                    .filter(v => v)
                    .length
            )
        },
        activeSearch: {
            get: () => state.activeSearch,
            getName: () => SearchEventTypes[state.activeSearch],
            set: (type: SearchEvent['type']) => (
                dispatch({ category: 'ACTION', type: 'ACTIVE.SEARCH', action: 'SET', payload: { activeSearch: type } })
            ),
            list: () => Object.entries(SearchEventTypes)
                .map(([ type, name ]) => (
                    {
                        type: type as keyof typeof SearchEventTypes,
                        name
                    }
                ))
        }
    }

    const componentProps = {
        data: api.data,
        selectionEvent: api.selection,
        collapseGroupKeys: api.action.groupCollapseKeys,
        collapseLegKeys: api.action.legCollapseKeys,
        onAddEntitiesToFlight: api.action.flight.addEntities,
        onPaxNameClicked: (paxData) => (
            api.action.paxModal.open(paxData)
        ),
        onIsnButtonClicked: (paxData) => (
            api.action.isnDrawer.open(paxData)
        ),
        isAddingToFlight: saving
    }

    return {
        api: api,
        componentProps: componentProps,
        render: {
            paxModal: () => (
                <Modal
                    title="Edit Passenger"
                    visible={api.action.paxModal.isOpen}
                    onCancel={api.action.paxModal.close}
                    footer={null}
                    width="50rem"
                    >
                    <PassengerEditor
                        title="Edit Passenger"
                        edit={true}
                        data={api.action.paxModal.paxData}
                        onCancel={api.action.paxModal.close}
                    />
                </Modal>
            ),
            addButton: (props) => {
                return (
                    <Button
                        disabled={
                            !api.selection('ENTITY').get().length || saving
                        }
                        type="primary"
                        onClick={api.action.flight.addSelectedEntities}
                        {...props}
                    >
                        Add {api.selection('ENTITY').get().length} Selected PAX/CGO
                    </Button>
                )
            },
            searchInput: (props) => {
                return (
                    <Input.Search
                        onChange={(e) => (
                            api.search.get(
                                api.activeSearch.get()
                            ).set(e.target.value)
                        )}
                        value={api.search.get(
                            api.activeSearch.get()
                        ).get()}
                        placeholder={`Search by ${api.activeSearch.getName().toLowerCase()}`}
                        enterButton
                        allowClear
                        {...props}
                        style={{
                            width: 300,
                            ...props?.style
                        }}
                    />
                )
            },
            searchTypeSelect: (props) => {
                return (
                    <Select
                        value={api.activeSearch.get()}
                        onChange={api.activeSearch.set}
                        {...props}
                    >
                        {api.activeSearch.list().map(({ type, name }) => (
                            <Select.Option key={type} value={type}>By {name}</Select.Option>
                        ))}
                    </Select>
                )
            },
            filtersAppliedAlert: (props) => {
                if (!api.search.filterApplied()) return null;

                return (
                    <Alert
                        showIcon
                        message={
                            <>
                                <span>Results are filtered</span>
                                <Button
                                    type="primary"
                                    size="small"
                                    style={{
                                        marginLeft: 12
                                    }}
                                    onClick={api.search.clearAll}
                                >Clear filter</Button>
                            </>
                        }
                        {...props}
                        style={{
                            marginBottom: 16,
                            ...(props?.style)
                        }}
                    />
                )
            },
            errorAlert: (props) => {
                if (!api.data.error) return null;

                return (
                    <Alert
                        showIcon
                        type="error"
                        message="Failed to load schedules"
                        description={cleanGraphQLErrorMsg(api.data.error)}
                        {...props}
                        style={{ marginBottom: 12, ...(props?.style) }}
                    />
                )
            }
        }
    }
}
